import React, { useMemo } from "react";
import { Button, Flex, Space, Tag, Tooltip, Typography } from "antd";
import { EyeOutlined, WarningOutlined } from "@ant-design/icons";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { UserProfile } from "../../../../Entities/UserProfile";
import { MatchResult } from "../../../../Entities/MatchResult";
import { MatchFilterType } from "./JobApplicationProfileMatchResult";
import { CHART_COLORS } from "../../../JobPostDetails/components/StatsAndAnalytics/utils/chartColors";

interface JobApplicationProfileMatchResultHeaderProps {
  userProfile: UserProfile | null;
  setIsResumeViewing: React.Dispatch<
    React.SetStateAction<{
      isResumeViewing: boolean;
      resumeUrl: string | null;
    }>
  >;
  profileMatchResult: MatchResult | null;
  activeFilter: MatchFilterType;
  onFilterChange: (filter: MatchFilterType) => void;
}

const JobApplicationProfileMatchResultHeader: React.FC<
  JobApplicationProfileMatchResultHeaderProps
> = ({ userProfile, setIsResumeViewing, profileMatchResult, activeFilter, onFilterChange }) => {
  const handleViewResume = () => {
    if (!userProfile) {
      console.error(
        "Job Application Summary -> ProfileMatch -> handleViewResume: Resume not found"
      );
      return;
    }
    const resumeUrl = `https://hiring-assistant-files.s3.ap-south-1.amazonaws.com/profile/${userProfile.userId}/${userProfile.filename}`;
    setIsResumeViewing({
      isResumeViewing: true,
      resumeUrl: resumeUrl,
    });
  };

  const statistics = useMemo(() => {
    if (!profileMatchResult) {
      return {
        total: 0,
        matched: 0,
        partial: 0,
        unmatched: 0,
      };
    }

    const results = profileMatchResult.results || [];
    return {
      total: results.length,
      matched: results.filter((r) => r.match_status === 1).length,
      partial: results.filter((r) => r.match_status === -1).length,
      unmatched: results.filter((r) => r.match_status === 0).length,
    };
  }, [profileMatchResult]);

  const chartData = useMemo(() => [
    { key: 'matched' as MatchFilterType, label: 'Matched', value: statistics.matched, color: CHART_COLORS.success },
    { key: 'partial' as MatchFilterType, label: 'Partial', value: statistics.partial, color: CHART_COLORS.warning },
    { key: 'unmatched' as MatchFilterType, label: 'Unmatched', value: statistics.unmatched, color: CHART_COLORS.error },
  ].filter(item => item.value > 0), [statistics]);

  const legendItems = [
    { key: 'all' as MatchFilterType, label: 'All', value: statistics.total, color: CHART_COLORS.primary },
    { key: 'matched' as MatchFilterType, label: 'Matched', value: statistics.matched, color: CHART_COLORS.success },
    { key: 'partial' as MatchFilterType, label: 'Partial', value: statistics.partial, color: CHART_COLORS.warning },
    { key: 'unmatched' as MatchFilterType, label: 'Unmatched', value: statistics.unmatched, color: CHART_COLORS.error },
  ];

  const hasData = statistics.total > 0;
  const displayChartData = hasData
    ? chartData
    : [{ key: 'placeholder' as MatchFilterType, label: 'No Data', value: 1, color: CHART_COLORS.placeholder }];

  // Get unmatched reasons for display
  const unmatchedReasons = useMemo(() => {
    if (!profileMatchResult?.results) return [];
    return profileMatchResult.results
      .filter((r) => r.match_status === 0 && r.reason)
      .map((r) => r.reason);
  }, [profileMatchResult]);

  return (
    <div className="job-application-profile-match-result-header-container">
      <div className="job-application-profile-match-result-header-content"
        style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: "16px", justifyContent: "space-between" }}>
        <div className="job-application-profile-match-result-header-title">
          <Typography.Title level={5} style={{ margin: 0 }}>
            Profile Match Summary
          </Typography.Title>
          <Typography.Text type="secondary">
            Review the <span style={{ fontWeight: "bold" }}>AI-powered</span>{" "}
            resume analysis for the candidate
          </Typography.Text>
          <Space direction="vertical" align="start" size={4} style={{ marginTop: "8px" }}>
            <Tag color={profileMatchResult?.status === "qualified" ? "green" : profileMatchResult?.status === "unqualified" ? "red" : "orange"}>
              {profileMatchResult?.status
                ? `Status: ${profileMatchResult.status.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())}`
                : "Status: Unknown"}
              {unmatchedReasons.length > 0 && (
                <Flex justify="flex-start" align="center" gap="8px">

                  <Tooltip
                    title={
                      <div>
                        <strong>Unmatched Requirements:</strong>
                        <ul style={{ margin: '8px 0 0 0', paddingLeft: 16 }}>
                          {unmatchedReasons.slice(0, 5).map((reason, idx) => (
                            <li key={idx} style={{ fontSize: 12 }}>{reason}</li>
                          ))}
                          {unmatchedReasons.length > 5 && (
                            <li style={{ fontSize: 12, fontStyle: 'italic' }}>
                              +{unmatchedReasons.length - 5} more...
                            </li>
                          )}
                        </ul>
                      </div>
                    }
                    placement="bottom"
                  >
                    <Typography.Text type="danger" style={{ fontSize: 12, cursor: 'pointer' }}>
                      <WarningOutlined style={{ marginRight: 4 }} />
                      {unmatchedReasons.length} requirement{unmatchedReasons.length > 1 ? 's' : ''} not matched
                    </Typography.Text>
                  </Tooltip>
                </Flex>
              )}
            </Tag>
            {userProfile && (
            <div className="job-application-profile-match-result-header-actions">
              <Button
                size="small"
                type="link"
                onClick={handleViewResume}
              >
                View Resume
              </Button>
            </div>
          )}
          </Space>

          
        </div>

        {profileMatchResult && (
          <div className="profile-match-chart-container">
            <div className="profile-match-chart-wrapper">
              <ResponsiveContainer width="100%" height={100}>
                <PieChart>
                  <Pie
                    data={displayChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={25}
                    outerRadius={35}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {displayChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="profile-match-chart-center">
                <Typography.Text strong style={{ fontSize: 16 }}>{statistics.total}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 10 }}>Total</Typography.Text>
              </div>
            </div>
            <div className="profile-match-chart-legend">
              {legendItems.map((item) => {
                // Disable if count is 0 (no items with this status)
                const isDisabled = item.value === 0;
                
                return (
                  <div
                    key={item.key}
                    className={`profile-match-legend-item ${activeFilter === item.key ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => {
                      if (!isDisabled) {
                        onFilterChange(item.key);
                      }
                    }}
                    style={activeFilter === item.key && !isDisabled ? { borderColor: item.color, backgroundColor: `${item.color}15` } : {}}
                  >
                    <span
                      className="profile-match-legend-color"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="profile-match-legend-text">
                      {item.label} ({item.value})
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>


    </div>
  );
};

export default JobApplicationProfileMatchResultHeader;
